// On-screen touch controls for play mode: a virtual joystick for movement
// and (3D only) an interact button. Camera look on touch is handled by
// FirstPersonController itself (a drag anywhere outside the joystick), not
// here - this file only covers what has no drag-anywhere equivalent.
//
// Movement is bridged the same way the keyboard already drives it:
// `dispatchVirtualKey` (../../input/inputState.ts) fires a real KeyboardEvent
// (KeyW/A/S/D by default, following the project's own key bindings) on
// `window`. Every keyboard-driven controller's keydown/keyup listener picks
// these up unmodified, migrated onto the shared input module or not - the
// joystick is a second input source for the same keys, not a parallel
// movement system. This is deliberately still a real DOM event, not a
// private write into the shared module's own state: `Tilemap2DScene.tsx`'s
// own private keydown listener depends on exactly this mechanism and is
// untouched by that module's consolidation, so only a real event reaches it.
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';

import { useEngineRuntimeStore } from '../../store/engineRuntimeStore';
import { dispatchVirtualKey } from '../../input/inputState';

/** True on devices whose primary pointer is a touchscreen (not a mouse with
 * an incidental touch digitiser) - the signal used to decide whether to show
 * on-screen controls at all. Re-evaluated on demand, not cached, since a
 * 2-in-1 laptop can dock/undock a keyboard without a page reload. */
export function isTouchPrimary(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches) return true;
  return navigator.maxTouchPoints > 0 && !window.matchMedia?.('(pointer: fine)').matches;
}

const JOYSTICK_RADIUS = 46;
const JOYSTICK_DEADZONE = 0.28;

/** Clamp a raw pointer offset from the joystick's centre to its radius, so
 * dragging past the base doesn't move the knob past its ring. Pure so it's
 * unit-testable without a DOM. */
export function clampToRadius(dx: number, dy: number, radius: number): { x: number; y: number } {
  const dist = Math.hypot(dx, dy);
  if (dist <= radius || dist === 0) return { x: dx, y: dy };
  return { x: (dx / dist) * radius, y: (dy / dist) * radius };
}

export interface HeldDirections {
  forward: boolean;
  back: boolean;
  left: boolean;
  right: boolean;
}

/** A knob offset (already clamped to the joystick radius) quantised into the
 * held-key set a keyboard would produce - past the deadzone in more than one
 * axis holds two keys at once, giving diagonal movement exactly like WASD
 * does. Pure so the quantisation (in particular the deadzone) is
 * unit-testable without simulating pointer events. */
export function directionFromKnob(x: number, y: number, radius: number, deadzone: number): HeldDirections {
  const nx = radius === 0 ? 0 : x / radius;
  const ny = radius === 0 ? 0 : y / radius;
  return {
    forward: ny < -deadzone,
    back: ny > deadzone,
    left: nx < -deadzone,
    right: nx > deadzone,
  };
}

interface DirectionKeys {
  forward: string;
  back: string;
  left: string;
  right: string;
}

/**
 * A draggable virtual joystick, fixed bottom-left. Reports its normalised
 * offset as synthetic key presses on the four movement bindings, so a
 * diagonal push holds two keys down at once exactly like a keyboard would.
 * `stopPropagation` on every pointer event keeps this touch from also being
 * read as a camera-look drag by the controller listening on `window`.
 */
function VirtualJoystick({ keys }: { keys: DirectionKeys }) {
  const baseRef = useRef<HTMLDivElement>(null);
  const [knob, setKnob] = useState({ x: 0, y: 0 });
  const activeRef = useRef(false);
  const pointerIdRef = useRef<number | null>(null);
  const heldRef = useRef({ forward: false, back: false, left: false, right: false });

  const setHeld = useCallback((next: { forward: boolean; back: boolean; left: boolean; right: boolean }) => {
    const prev = heldRef.current;
    if (next.forward !== prev.forward) dispatchVirtualKey(next.forward ? 'keydown' : 'keyup', keys.forward);
    if (next.back !== prev.back) dispatchVirtualKey(next.back ? 'keydown' : 'keyup', keys.back);
    if (next.left !== prev.left) dispatchVirtualKey(next.left ? 'keydown' : 'keyup', keys.left);
    if (next.right !== prev.right) dispatchVirtualKey(next.right ? 'keydown' : 'keyup', keys.right);
    heldRef.current = next;
  }, [keys]);

  const release = useCallback(() => {
    activeRef.current = false;
    pointerIdRef.current = null;
    setKnob({ x: 0, y: 0 });
    setHeld({ forward: false, back: false, left: false, right: false });
  }, [setHeld]);

  useEffect(() => () => release(), [release]);

  const updateFromClient = useCallback((clientX: number, clientY: number) => {
    const base = baseRef.current;
    if (!base) return;
    const rect = base.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const clamped = clampToRadius(clientX - cx, clientY - cy, JOYSTICK_RADIUS);
    setKnob(clamped);
    setHeld(directionFromKnob(clamped.x, clamped.y, JOYSTICK_RADIUS, JOYSTICK_DEADZONE));
  }, [setHeld]);

  return (
    <div
      ref={baseRef}
      onPointerDown={(e) => {
        e.stopPropagation();
        (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
        activeRef.current = true;
        pointerIdRef.current = e.pointerId;
        updateFromClient(e.clientX, e.clientY);
      }}
      onPointerMove={(e) => {
        if (!activeRef.current || e.pointerId !== pointerIdRef.current) return;
        e.stopPropagation();
        updateFromClient(e.clientX, e.clientY);
      }}
      onPointerUp={(e) => {
        e.stopPropagation();
        release();
      }}
      onPointerCancel={(e) => {
        e.stopPropagation();
        release();
      }}
      style={{
        position: 'absolute',
        left: 26,
        bottom: 26,
        width: JOYSTICK_RADIUS * 2,
        height: JOYSTICK_RADIUS * 2,
        borderRadius: '50%',
        background: 'rgba(20, 20, 26, 0.4)',
        border: '1px solid rgba(255,255,255,0.18)',
        touchAction: 'none',
        pointerEvents: 'auto',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          width: 42,
          height: 42,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.28)',
          border: '1px solid rgba(255,255,255,0.4)',
          transform: `translate(calc(-50% + ${knob.x}px), calc(-50% + ${knob.y}px))`,
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}

/** A momentary button that pulses a synthetic key press - for the actions
 * (interact, primary action) that have no drag/joystick equivalent. */
function TouchActionButton({ code, label, style }: { code: string; label: string; style?: CSSProperties }) {
  return (
    <button
      type="button"
      aria-label={label}
      onPointerDown={(e) => {
        e.stopPropagation();
        dispatchVirtualKey('keydown', code);
      }}
      onPointerUp={(e) => {
        e.stopPropagation();
        dispatchVirtualKey('keyup', code);
      }}
      onPointerCancel={(e) => {
        e.stopPropagation();
        dispatchVirtualKey('keyup', code);
      }}
      style={{
        position: 'absolute',
        width: 58,
        height: 58,
        borderRadius: '50%',
        background: 'rgba(20, 20, 26, 0.4)',
        border: '1px solid rgba(255,255,255,0.18)',
        color: 'rgba(255,255,255,0.85)',
        fontFamily: 'inherit',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        touchAction: 'none',
        pointerEvents: 'auto',
        ...style,
      }}
    >
      {label}
    </button>
  );
}

/**
 * Whether the mobile camera-toggle button should render: only in 3D, and
 * only when nothing forces the camera mode - a creator's Game Setup choice
 * makes the desktop `V` key a no-op too in that case (see
 * FirstPersonController.tsx's `cameraModeForced` handling), so a visible
 * button that does nothing would be worse than no button. Pure so it's
 * unit-testable without mounting the overlay.
 */
export function shouldShowCameraToggle(
  projection: '2D' | '3D',
  cameraModeForced: 'first' | 'third' | null,
): boolean {
  return projection === '3D' && cameraModeForced === null;
}

export interface TouchControlsOverlayProps {
  /** 2D has no camera to look with and no interact system yet - only the
   * movement joystick renders. 3D adds the interact button; look is handled
   * by FirstPersonController's own touch-drag support, not rendered here. */
  projection: '2D' | '3D';
}

/**
 * Mounted by GameRuntime in play mode on touch-primary devices. A no-op
 * (returns null) on desktop/mouse devices - checked once per mount via
 * `isTouchPrimary()`, not on every render, since input capability doesn't
 * change mid-session on the devices this actually matters for.
 */
export function TouchControlsOverlay({ projection }: TouchControlsOverlayProps) {
  const keyBindings = useEngineRuntimeStore((s) => s.keyBindings);
  const cameraModeForced = useEngineRuntimeStore((s) => s.cameraModeForced);
  const [show] = useState(isTouchPrimary);

  // The 2D top-down controller (Tilemap2DScene) reads fixed KeyW/A/S/D or
  // arrow codes directly, not the configurable bindings store - the joystick
  // has to target what it actually listens for. FirstPersonController (3D)
  // does read the configurable bindings, so it gets those instead.
  //
  // Memoized on the underlying key codes (not just recomputed inline every
  // render): VirtualJoystick's release effect re-fires its cleanup whenever
  // this object's identity changes, which would otherwise snap an
  // in-progress drag back to centre on any unrelated re-render (this
  // component also subscribes to cameraModeForced, which changes for
  // reasons that have nothing to do with movement keys).
  const movementKeys: DirectionKeys = useMemo(
    () =>
      projection === '2D'
        ? { forward: 'KeyW', back: 'KeyS', left: 'KeyA', right: 'KeyD' }
        : {
            forward: keyBindings.moveForward,
            back: keyBindings.moveBack,
            left: keyBindings.moveLeft,
            right: keyBindings.moveRight,
          },
    [projection, keyBindings.moveForward, keyBindings.moveBack, keyBindings.moveLeft, keyBindings.moveRight],
  );

  if (!show) return null;

  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 20 }}>
      <VirtualJoystick keys={movementKeys} />
      {projection === '3D' && (
        <TouchActionButton code={keyBindings.interact} label="Use" style={{ right: 26, bottom: 96 }} />
      )}
      {shouldShowCameraToggle(projection, cameraModeForced) && (
        <TouchActionButton code={keyBindings.toggleCamera} label="View" style={{ right: 26, bottom: 166 }} />
      )}
    </div>
  );
}
